import { Link as RouterLink } from 'react-router-dom';

import { LWApiPublicNotificationDetailsResultRawI } from '@liquidweb-dev/club-sauce/public/notifications/index.raw';
import Box from '@liquidweb-dev/undercurrent/Box';
import ListItem, {
	type ListItemProps,
} from '@liquidweb-dev/undercurrent/ListItem';
import ListItemButton from '@liquidweb-dev/undercurrent/ListItemButton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import { pseudoClasses } from '@liquidweb-dev/undercurrent/classes/pseudo';
import type { AssetDetails } from 'modules/queries/asset/useDetails';
import ActionsMenu from 'components/standard/HostingDetailsActions/ActionsMenu';
import ServerStatusBadge from 'components/standard/ServerStatusBadge';
import { getStatusProps } from 'components/standard/ServerStatusBadgeFlat';

export interface ServersListRowProps extends ListItemProps {
	assetDetails: AssetDetails;
	notification?: LWApiPublicNotificationDetailsResultRawI;
}

const ServersListRow = ({
	assetDetails,
	notification,
	children,
	...rest
}: ServersListRowProps) => {
	const {
		custom_name: customName,
		domain,
		ip,
		isPrivateParent,
		path,
		powerStatus,
		status,
	} = assetDetails;
	const { last_alert: lastAlert, description } = notification || {};

	const { color } = getStatusProps({
		status,
		powerStatus,
	});

	const getColor = (statusColor: string) => {
		if (statusColor === 'danger' || statusColor === 'warning') {
			return statusColor;
		}
		return undefined;
	};

	return (
		<ListItem color={getColor(color)} {...rest}>
			<ListItemButton
				component={RouterLink}
				to={path}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 300,
					[pseudoClasses.important]: {
						padding: 200,
					},
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 300,
						color: 'initial',
						textDecoration: 'none',
						flex: '1 0 0',
						minWidth: 0,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							gap: 200,
							alignItems: 'center',
							maxWidth: '320px',
							flex: '0 0 auto',
							minWidth: 0,
						}}
					>
						<ServerStatusBadge
							status={status}
							powerStatus={powerStatus}
							notificationPrimary={description}
							notificationSecondary={lastAlert || ''}
							sx={{ position: 'absolute' }}
						/>

						<Stack
							sx={{ gap: 100, flex: '1 0 0', minWidth: 0, marginLeft: '40px' }}
						>
							<Box sx={{ display: 'flex', gap: 100, alignItems: 'center' }}>
								<TypographyOverflow variant="label-lg" sx={{ fontWeight: 500 }}>
									{customName || domain}
								</TypographyOverflow>

								{!isPrivateParent && (
									<Typography
										variant="label-md"
										sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
									>
										{ip}
									</Typography>
								)}
							</Box>

							<TypographyOverflow
								variant="label-md"
								sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
							>
								{domain}
							</TypographyOverflow>
						</Stack>
					</Box>

					<Box
						sx={{
							display: 'flex',
							gap: 300,
							alignItems: 'center',
							justifyContent: 'flex-end',
							flex: '1 1 0',
							minWidth: 0,
						}}
					>
						{children}
					</Box>
				</Box>

				<Box
					onClick={(e) => {
						// prevents all events from bubbling up to the list item button (which would cause the link to be followed)
						e.stopPropagation();
					}}
				>
					<ActionsMenu
						{...assetDetails}
						intent="serversList"
						iconButtonProps={{
							onClick: (e) => {
								// for some reason also need to prevent default on the button to prevent the link from being followed
								e.preventDefault();
							},
							variant: 'ghost',
							size: 'small',
							sx: {
								flexShrink: 0,
								flexGrow: 0,
							},
						}}
					/>
				</Box>
			</ListItemButton>
		</ListItem>
	);
};

export default ServersListRow;
